import * as React from 'react'
import { Form as FinalForm } from 'react-final-form'
import { useMutation } from '@apollo/client'
import { capitalize } from 'lodash'
import moment from 'moment-timezone'
import { useFeature } from 'toggled'

import { createCustomerComplaintMutation } from '~/gql/mutations'
import { useFetcher } from '~/hooks'
import useVendor from '~/hooks/use-vendor'
import notifier from '~/truck/notifier'
import { getS3SignedForm, uploadToS3 } from '~/utils'
import { flags, uploadFolders } from '~/utils/constants'
import formValidators from '~/utils/form-validators'
import handleSubmissionErrors from '~/utils/handle-submission-errors'

import { Container } from './elements'
import Form from './form'
import Header from './header'
import SuccessState from './success-state'

moment.locale('es')

const now = moment()

const formattedNow = `${capitalize(now.format('dddd'))}, ${now.format('LL')}`

const initialValues = {
  assetType: 'product',
  complainType: 'claim',
  isUnderage: false,
}

const CustomerComplaintsScreen = () => {
  const [createCustomerComplaint] = useMutation(createCustomerComplaintMutation)

  const vendor = useVendor()

  const fetcher = useFetcher()

  const customerComplaintsFF = useFeature(flags.CUSTOMER_COMPLAINTS)

  const purchaseChannels = customerComplaintsFF?.settings?.purchaseChannels

  const [submitted, setSubmitted] = React.useState(false)

  const [complaintCode, setComplaintCode] = React.useState(null)

  if (submitted) {
    return (
      <Container style={{ justifyContent: 'center', alignItems: 'center' }}>
        <SuccessState code={complaintCode} />
      </Container>
    )
  }

  const onSubmit = async variables => {
    try {
      if (variables.fileAttachment) {
        const signedForm = await getS3SignedForm(
          variables.fileAttachment,
          uploadFolders.CUSTOMER_COMPLAIN_ATTACHMENTS,
          fetcher,
        )

        await uploadToS3(signedForm, variables.fileAttachment)

        variables.attachmentFilename = signedForm?.fields?.filename

        variables.attachmentKey = signedForm?.fields?.key
      }

      const { data } = await createCustomerComplaint({
        variables: {
          ...variables,
          isUnderAge: variables.isUnderage,
          claimedAmount: parseFloat(variables.claimedAmount),
        },
      })

      setComplaintCode(data.createCustomerComplaint.customerComplaint.code)

      setSubmitted(true)
    } catch (error) {
      notifier.error('Ocurrió un error al crear el reclamo, intente nuevamente.')

      return handleSubmissionErrors(error)
    }
  }

  const validate = formValues => {
    const errors = formValidators.customerComplaint(formValues)

    const { privacyPolicyAccepted } = formValidators.privacyPolicyAccepted({
      vendor,
      privacyPolicyAccepted: formValues.privacyPolicyAccepted,
    })

    errors.privacyPolicyAccepted = privacyPolicyAccepted

    if (purchaseChannels) {
      const { purchaseChannel } = formValidators.purchaseChannel({
        purchaseChannel: formValues.purchaseChannel,
      })

      errors.purchaseChannel = purchaseChannel
    }

    return errors
  }

  return (
    <FinalForm initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
      {formProps => {
        return (
          <Container>
            <Header />
            <div className="flex rounded bg-neutral-100 p-4">
              <p className="mr-auto text-xs">{formattedNow}</p>
            </div>
            <Form vendor={vendor} {...formProps} />
          </Container>
        )
      }}
    </FinalForm>
  )
}

export default CustomerComplaintsScreen
